import { createContext, useContext } from 'react';
import { z } from 'zod';

import { type ErrorCode, ErrorResponse } from './generated/schemas.ts';

// Error bodies are JSON from the API, but a proxy or crash can answer with plain text or HTML.
const parseJson = (text: string): unknown => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

export class ApiError extends Error {
    status: number;
    // What went wrong, translated as `errors.api.<code>`; null when the answer didn't come from the API (e.g. a proxy).
    code: ErrorCode | null;
    // The input field the error is about, e.g. 'slug' when a slug is taken.
    field: string | null;
    // The request it answered ('POST /api/…'), for the error log a reported problem sends along (plan task 98).
    request: string | null;

    constructor(
        status: number,
        code: ErrorCode | null,
        message: string,
        field: string | null = null,
        request: string | null = null
    ) {
        super(message);
        this.status = status;
        this.code = code;
        this.field = field;
        this.request = request;
    }

    // The server's message is for developers; users get the translation of the code (see Form and useAsyncAction).
    get messageKey() {
        return this.code ? (`errors.api.${this.code}` as const) : 'errors.failed';
    }
}

// Django checks the CSRF token on requests that change something (api/accounts/auth.py); it sets it as a cookie.
const readCsrfToken = () =>
    document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('recap_csrftoken='))
        ?.slice('recap_csrftoken='.length) ?? '';

type ApiFetchInit = RequestInit & { schema?: z.ZodType };

// The generated request functions (generated/<app>.ts) call this with the response's schema, so a drifting backend
// contract fails loudly instead of rendering garbage. Endpoints without a body (204) have no schema.
export const apiFetch = async <Result>(path: string, { schema, ...init }: ApiFetchInit = {}): Promise<Result> => {
    // FormData bodies (file uploads) need the browser to set the multipart boundary itself.
    const isFormData = init?.body instanceof FormData;
    const response = await fetch(`/api${path}`, {
        ...init,
        headers: {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            'X-CSRFToken': readCsrfToken(),
            ...init?.headers,
        },
    });
    if (!response.ok) {
        const text = await response.text();
        const body = ErrorResponse.safeParse(parseJson(text));
        const request = `${init.method ?? 'GET'} /api${path}`;
        throw body.success
            ? new ApiError(response.status, body.data.code, body.data.message, body.data.field, request)
            : new ApiError(response.status, null, text, null, request);
    }
    if (response.status === 204) return undefined as Result;
    const body: unknown = await response.json();
    return (schema ? schema.parse(body) : body) as Result;
};

// The id of the URL's workspace (/w/:workspaceSlug/…), which AppLayout provides; null outside of one.
export const WorkspaceIdContext = createContext<string | null>(null);

// The generated hooks call a workspace's endpoints as /workspace/… (orval.config.ts); this puts the id of the URL's
// workspace back.
export const useApiFetch = () => {
    const workspaceId = useContext(WorkspaceIdContext);
    return <Result>(path: string, init?: ApiFetchInit): Promise<Result> => {
        if (!path.startsWith('/workspace/')) return apiFetch<Result>(path, init);
        if (workspaceId === null) throw new Error(`${path} is called outside of a workspace`);
        return apiFetch<Result>(`/workspaces/${workspaceId}${path.slice('/workspace'.length)}`, init);
    };
};

// What the generated hooks type their errors as: apiFetch throws an ApiError for every error body.
export type ErrorType<_Body> = ApiError;

// Mutations that take away what the current page shows (deleting it, merging it, approving it out of the review queue)
// get a callback that leaves the page. It's awaited before the cache is updated, so the page is gone by then and never
// refetches something that no longer exists.
export type LeavePage<Result = void> = (result: Result) => void | Promise<void>;
